/**
 * SEO and marketing utility functions for Decadent Palete
 */

// Generate sitemap URLs for better search engine indexing
export const generateSitemapUrls = () => {
  const baseUrl = 'https://decadentpalete.com';
  const currentDate = new Date().toISOString().split('T')[0];
  
  const urls = [
    {
      loc: baseUrl + '/',
      lastmod: currentDate,
      changefreq: 'weekly',
      priority: '1.0'
    },
    {
      loc: baseUrl + '/services',
      lastmod: currentDate,
      changefreq: 'weekly', 
      priority: '0.9'
    },
    {
      loc: baseUrl + '/wellness-retreat',
      lastmod: currentDate,
      changefreq: 'monthly',
      priority: '0.8'
    },
    {
      loc: baseUrl + '/travel-with-chef',
      lastmod: currentDate,
      changefreq: 'monthly',
      priority: '0.8'
    },
    {
      loc: baseUrl + '/corporate',
      lastmod: currentDate,
      changefreq: 'monthly',
      priority: '0.7'
    },
    {
      loc: baseUrl + '/about',
      lastmod: currentDate,
      changefreq: 'monthly',
      priority: '0.6'
    },
    {
      loc: baseUrl + '/careers',
      lastmod: currentDate,
      changefreq: 'weekly',
      priority: '0.6'
    },
    {
      loc: baseUrl + '/testimony',
      lastmod: currentDate,
      changefreq: 'weekly',
      priority: '0.7'
    },
    {
      loc: baseUrl + '/booking',
      lastmod: currentDate,
      changefreq: 'daily',
      priority: '0.9'
    },
    {
      loc: baseUrl + '/inquiry',
      lastmod: currentDate,
      changefreq: 'monthly',
      priority: '0.5'
    },
    {
      loc: baseUrl + '/faqs',
      lastmod: currentDate,
      changefreq: 'monthly',
      priority: '0.4'
    },
    {
      loc: baseUrl + '/member-policy',
      lastmod: currentDate,
      changefreq: 'yearly',
      priority: '0.3'
    }
  ];
  
  return urls;
};

// Generate XML sitemap content
export const generateSitemapXML = () => {
  const urls = generateSitemapUrls();
  
  let xml = '<?xml version="1.0" encoding="UTF-8"?>\n';
  xml += '<urlset xmlns="http://www.sitemaps.org/schemas/sitemap/0.9">\n';
  
  urls.forEach(url => {
    xml += '  <url>\n';
    xml += `    <loc>${url.loc}</loc>\n`;
    xml += `    <lastmod>${url.lastmod}</lastmod>\n`;
    xml += `    <changefreq>${url.changefreq}</changefreq>\n`;
    xml += `    <priority>${url.priority}</priority>\n`;
    xml += '  </url>\n';
  });
  
  xml += '</urlset>';
  
  return xml;
};

// Generate robots.txt content
export const generateRobotsTxt = () => {
  return `User-agent: *
Allow: /

# Sitemap
Sitemap: https://decadentpalete.com/sitemap.xml

# Disallow admin areas
Disallow: /admin
Disallow: /customer-portal

# Allow important pages
Allow: /services
Allow: /wellness-retreat
Allow: /travel-with-chef
Allow: /corporate
Allow: /about
Allow: /careers
Allow: /testimony
Allow: /booking
Allow: /inquiry

# Crawl delay (be respectful)
Crawl-delay: 1`;
};

// Track page views for analytics
export const trackPageView = (pageName, additionalData = {}) => {
  // Google Analytics 4 tracking
  if (typeof gtag !== 'undefined') {
    gtag('config', 'GA_MEASUREMENT_ID', {
      page_title: pageName,
      page_location: window.location.href,
      ...additionalData
    });
  }
  
  // Custom analytics tracking
  if (window.analytics && typeof window.analytics.track === 'function') {
    window.analytics.track('Page Viewed', {
      page: pageName,
      url: window.location.href,
      referrer: document.referrer,
      timestamp: new Date().toISOString(),
      ...additionalData
    });
  }
  
  // Facebook Pixel
  if (typeof fbq !== 'undefined') {
    fbq('track', 'PageView', {
      content_name: pageName,
      ...additionalData
    });
  }
};

// Track conversions for marketing campaigns
export const trackConversion = (eventName, eventData = {}) => {
  // Google Analytics conversion tracking
  if (typeof gtag !== 'undefined') {
    gtag('event', eventName, {
      event_category: 'engagement',
      event_label: eventData.label || eventName,
      value: eventData.value || 1,
      ...eventData
    });
  }
  
  // Facebook Pixel conversion tracking
  if (typeof fbq !== 'undefined') {
    fbq('track', eventName, eventData);
  }
  
  // Custom conversion tracking
  console.log('Conversion tracked:', eventName, eventData);
};

// Generate rich snippets for search results
export const generateBreadcrumbSchema = (breadcrumbs) => {
  return {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    "itemListElement": breadcrumbs.map((crumb, index) => ({
      "@type": "ListItem",
      "position": index + 1,
      "name": crumb.name,
      "item": `https://decadentpalete.com${crumb.url}`
    }))
  };
};

// Generate FAQ schema for better search visibility  
export const generateFAQSchema = (faqs) => {
  return {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    "mainEntity": faqs.map(faq => ({
      "@type": "Question",
      "name": faq.question,
      "acceptedAnswer": {
        "@type": "Answer",
        "text": faq.answer
      }
    }))
  };
};

// Generate event schema for bookings and experiences
export const generateEventSchema = (event) => {
  return {
    "@context": "https://schema.org",
    "@type": "Event",
    "name": event.name || "Caribbean Soul Cuisine Experience",
    "description": event.description || "Luxury private chef experience with Caribbean soul cuisine",
    "startDate": event.startDate,
    "endDate": event.endDate,
    "location": {
      "@type": "Place",
      "name": event.locationName || "Private Location",
      "address": event.address || "Luxury Private Chef Services"
    },
    "organizer": {
      "@type": "Organization",
      "name": "Decadent Palete",
      "url": "https://decadentpalete.com"
    },
    "offers": {
      "@type": "Offer",
      "name": event.offerName || "Private Chef Experience",
      "description": event.offerDescription || "Luxury Caribbean soul cuisine experience",
      "availability": "https://schema.org/InStock",
      "validFrom": new Date().toISOString()
    }
  };
};

// Performance optimization utilities
export const preloadCriticalResources = () => {
  // Preload hero images
  const heroImages = [
    '/images/hero-caribbean-cuisine.jpg',
    '/images/chef-cooking.jpg',
    '/images/wellness-retreat.jpg'
  ];
  
  heroImages.forEach(src => {
    const link = document.createElement('link');
    link.rel = 'preload';
    link.as = 'image';
    link.href = src;
    document.head.appendChild(link);
  });
};

// Social sharing utilities
export const generateShareUrls = (title, description, url) => {
  const encodedTitle = encodeURIComponent(title);
  const encodedDescription = encodeURIComponent(description);
  const encodedUrl = encodeURIComponent(url);
  
  return {
    facebook: `https://www.facebook.com/sharer/sharer.php?u=${encodedUrl}&quote=${encodedTitle}`,
    twitter: `https://twitter.com/intent/tweet?text=${encodedTitle}&url=${encodedUrl}`,
    linkedin: `https://www.linkedin.com/sharing/share-offsite/?url=${encodedUrl}`,
    pinterest: `https://pinterest.com/pin/create/button/?url=${encodedUrl}&description=${encodedDescription}`,
    whatsapp: `https://wa.me/?text=${encodedTitle}%20${encodedUrl}`,
    email: `mailto:?subject=${encodedTitle}&body=${encodedDescription}%0A%0A${encodedUrl}`
  };
};